import { lazy, Suspense } from "react";
import { FACE_BY_ID } from "~/lib/faces";
import { useMarkFaceVisited } from "~/lib/hooks/useMarkFaceVisited";
import { useFaceSeo } from "~/lib/seo/useSeo";
import { ChatPanel } from "~/ui/AIChat/ChatPanel";

const AIStage = lazy(() =>
  import("~/three/AIStage").then((m) => ({ default: m.AIStage })),
);

export function AI() {
  const face = FACE_BY_ID.ai;
  useMarkFaceVisited(face.id);
  useFaceSeo(face);

  return (
    <section className="flex w-full flex-col gap-10 pb-16">
      <div className="relative h-48 w-full overflow-hidden border-b border-jg-border/40 sm:h-56">
        <Suspense
          fallback={
            <div
              aria-hidden="true"
              className="absolute inset-0 bg-gradient-to-b from-jg-ai/15 to-transparent"
            />
          }
        >
          <AIStage />
        </Suspense>

        <div className="pointer-events-none absolute right-4 top-4 sm:right-6">
          <span
            className="font-mono text-xs uppercase tracking-[0.25em]"
            style={{ color: face.themeColor }}
          >
            Cubie · {face.label}
          </span>
        </div>
      </div>

      <header className="mx-auto w-full max-w-3xl px-4">
        <h1 className="font-display text-3xl font-semibold leading-tight tracking-tight text-jg-fg sm:text-4xl">
          <span style={{ color: face.themeColor }}>LLM apps</span>, agentic
          workflows, model integration
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-jg-fg-muted sm:text-base">
          Chat with João's site assistant below — it runs Gemini Flash
          through the JGson Hono backend, rate-limited per IP. Wiring
          this kind of streaming LLM into your own product is exactly
          the kind of build João ships for clients.
        </p>
      </header>

      <ChatPanel />

      <div className="mx-auto flex w-full max-w-3xl flex-col gap-8 px-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-2xl border border-jg-border bg-jg-bg-soft/40 p-5">
            <p
              className="font-mono text-[10px] uppercase tracking-[0.25em]"
              style={{ color: face.themeColor }}
            >
              What we build
            </p>
            <ul className="mt-3 space-y-2 text-sm text-jg-fg-muted">
              <li>
                <span className="text-jg-fg">LLM apps:</span> Claude /
                OpenAI integration, RAG pipelines, tool use, agentic loops,
                streaming chat surfaces.
              </li>
              <li>
                <span className="text-jg-fg">n8n workflows:</span> the
                automation glue — webhook ingestion → LLM call → multi-step
                logic → outbound action. Self-hosted or n8n.cloud.
              </li>
              <li>
                <span className="text-jg-fg">Automatic senders:</span> drip
                campaigns, broadcast bots, scheduled outreach. Email,
                WhatsApp, Telegram, Slack — pick a channel or use all four
                from one workflow.
              </li>
              <li>
                <span className="text-jg-fg">CRM &amp; event responders:</span>{" "}
                lead arrives → enrich → tag → respond via the right channel
                → log. All inside n8n, with the LLM doing the routing
                judgement calls.
              </li>
            </ul>
          </div>

          <div className="rounded-2xl border border-jg-border bg-jg-bg-soft/40 p-5">
            <p
              className="font-mono text-[10px] uppercase tracking-[0.25em]"
              style={{ color: face.themeColor }}
            >
              Stack
            </p>
            <ul className="mt-3 flex flex-wrap gap-1.5">
              {[
                "Claude API",
                "OpenAI",
                "n8n",
                "Resend",
                "SendGrid",
                "WhatsApp Business API",
                "Telegram Bot",
                "Slack API",
                "Cloudflare Workers AI",
                "PostgreSQL",
                "Vectorize",
                "Webhooks",
              ].map((t) => (
                <li
                  key={t}
                  className="rounded-full border border-jg-border bg-jg-bg/40 px-2.5 py-0.5 text-xs text-jg-fg-muted"
                >
                  {t}
                </li>
              ))}
            </ul>
            <p className="mt-4 text-xs leading-relaxed text-jg-fg-muted">
              n8n is the connector layer; the LLM adds the judgement step.
              Most builds skip both a SaaS subscription and a hand-rolled
              microservice — just a workflow that any client can read.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
